import React from "react";
import { Link } from "react-router-dom";
import {
  FaGem,
  FaKey,
  FaConciergeBell,
  FaCarSide,
  FaShieldAlt,
  FaArrowRight,
  FaStar,
  FaCheckCircle,
} from "react-icons/fa";
// Ensure you have an appropriate luxury car image or use a solid background fallback
import heroImage from "../../assets/react-cars-hero-bg.jpg";

const luxuryClasses = [
  {
    name: "Executive Sedans",
    models: "Mercedes-Benz E-Class, BMW 5 Series, Audi A6",
    desc: "Perfect for business travel or anniversary dinners. Experience whisper-quiet cabins, premium leather, and advanced technology.",
  },
  {
    name: "Premium SUVs",
    models: "Range Rover, Cadillac Escalade, BMW X7",
    desc: "Command the road with ultimate comfort and space. Ideal for luxury family vacations or group trips to the mountains.",
  },
  {
    name: "Sports & Performance",
    models: "Porsche 911, Chevy Corvette, Ford Mustang GT",
    desc: "Turn heads and feel the thrill. High-performance engines and striking designs for an unforgettable driving experience.",
  },
];

const faqs = [
  {
    q: "How old do I need to be to rent a luxury vehicle?",
    a: "Due to the high value of premium vehicles, most rental agencies require drivers to be at least 25 years old to rent from their luxury or exotic fleets. Some specific high-performance models may require you to be 30 or older.",
  },
  {
    q: "Is a larger security deposit required?",
    a: "Yes. Rental companies typically place a higher credit card hold (deposit) for luxury and premium vehicles compared to standard cars. A major credit card in the primary driver's name is strictly required.",
  },
  {
    q: "Am I guaranteed the exact make and model?",
    a: "Many standard rentals are 'or similar', but our partners' premium collections often offer an 'Exact Model Guarantee'. Check the specific vehicle listing during checkout to see if the make and model are guaranteed.",
  },
];

export default function LuxuryCarRental() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* HERO SECTION */}
      <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden bg-slate-950 px-4 py-20 sm:px-6">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Luxury Car Rental"
            className="h-full w-full object-cover object-center opacity-30 grayscale"
          />
          {/* Amber/Gold tinted gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-900/80 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <div className="mb-6 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-amber-500 backdrop-blur-md">
              <FaGem className="text-amber-500" /> Premium Collection
            </span>
          </div>
          <h1 className="mb-6 text-5xl font-black leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
            Arrive in <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600">
              Absolute Style.
            </span>
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg font-medium leading-relaxed text-slate-300 sm:text-xl">
            Make a statement on your next trip. Drive top-tier European sedans,
            premium sports cars, and luxury SUVs from the world's finest
            manufacturers.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/cars"
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-8 py-4 text-base font-black text-slate-950 shadow-[0_0_40px_rgba(251,191,36,0.3)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_60px_rgba(251,191,36,0.5)] sm:w-auto"
            >
              Find Premium Vehicles
              <FaArrowRight className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BANNER */}
      <section className="border-b border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-8 px-4 sm:gap-16 sm:px-6">
          {[
            { icon: <FaStar />, text: "Latest Models" },
            { icon: <FaConciergeBell />, text: "White-Glove Service" },
            { icon: <FaCheckCircle />, text: "Exact Model Guarantees" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 text-slate-700">
              <div className="text-xl text-amber-500">{item.icon}</div>
              <span className="text-sm font-bold uppercase tracking-wide">
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION GRID */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            The VIP Experience
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base font-medium text-slate-500">
            Elevate your journey with amenities and services designed for the
            most discerning travelers.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              icon: <FaGem />,
              title: "Elite Manufacturers",
              desc: "Experience the engineering perfection of Mercedes-Benz, BMW, Porsche, Audi, and Land Rover. Why settle for standard?",
            },
            {
              icon: <FaKey />,
              title: "Priority Service",
              desc: "Skip the standard counter lines. Many luxury rentals offer dedicated VIP pickup areas or delivery directly to the terminal curb.",
            },
            {
              icon: <FaShieldAlt />,
              title: "Unmatched Comfort",
              desc: "Indulge in ventilated leather seats, panoramic sunroofs, adaptive cruise control, and studio-quality surround sound systems.",
            },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="group rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-amber-200 hover:shadow-[0_20px_40px_rgba(251,191,36,0.1)]"
            >
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-3xl text-amber-500 transition-transform duration-300 group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-white">
                {feature.icon}
              </div>
              <h3 className="mb-3 text-xl font-black text-slate-900">
                {feature.title}
              </h3>
              <p className="text-sm font-medium leading-relaxed text-slate-500">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* THE FLEET */}
      <section className="bg-slate-900 py-20 text-white sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl">
              Discover the Fleet
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base font-medium text-slate-400">
              Browse the categories available in our partners' premium
              collections.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {luxuryClasses.map((carClass, idx) => (
              <div
                key={idx}
                className="flex flex-col justify-between rounded-[2rem] border border-white/10 bg-white/5 p-8 transition-colors hover:border-amber-500/50 hover:bg-white/10"
              >
                <div>
                  <div className="mb-4 text-3xl text-amber-500">
                    <FaCarSide />
                  </div>
                  <h3 className="mb-2 text-2xl font-bold">{carClass.name}</h3>
                  <p className="mb-6 text-sm font-medium text-slate-400">
                    {carClass.desc}
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-500">
                    Typical Models
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-300">
                    {carClass.models}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            Luxury Rental FAQs
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group rounded-2xl border border-slate-200 bg-[#f8f9fc] p-6 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-slate-900">
                  {faq.q}
                  <span className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-amber-500 shadow-sm transition-transform group-open:rotate-180">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-sm font-medium leading-relaxed text-slate-600">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="border-t-[4px] border-amber-500 bg-slate-950 py-16 text-center text-white sm:py-20">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="mb-6 text-3xl font-black sm:text-4xl">
            Upgrade Your Journey Today
          </h2>
          <p className="mb-8 text-lg font-medium text-slate-400">
            Search live availability for luxury sedans, SUVs, and performance
            vehicles at your destination.
          </p>
          <Link
            to="/cars"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-8 py-4 font-black text-slate-950 shadow-lg shadow-amber-500/20 transition-transform hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-500/30 active:translate-y-0"
          >
            Find Your Dream Car
            <FaArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
}
